// src/api/preferences.ts
/**
 * Frontend API client for preferences management.
 *
 * Public routes (all authenticated users):
 * - listColorPresets
 * - getEffectivePreferences
 *
 * Platform admin routes (require superadmin):
 * - getPlatformPreferences
 * - updatePlatformPreferences
 *
 * Account admin routes:
 * - getAccountPreferences
 * - updateAccountPreferences
 * - clearAccountPreferences
 * - getReadingProfiles
 * - putReadingProfiles
 */

import type { components } from "@verity/shared-types";
import { apiJson, apiData } from "./client";
import type { TextStylesDTO, TextStylesSourceDTO, TextStylesWrite } from "./dashboards";

// ============================================================================
// Types
// ============================================================================

export type ChartColorPreset =
  | "monochrome"
  | "dual_tone"
  | "ocean"
  | "sunset"
  | "corporate"
  | "custom";

export type ChartTypePreference = "line" | "bar" | "auto";

export type DateFormatPreset = "short" | "medium" | "long" | "iso";

export type DateOrderPreset = "dmy" | "mdy" | "ymd";

/**
 * One attribution model as the pickers offer it: value, label, and whether
 * this account can choose it (with the reason when it cannot).
 *
 * There is deliberately no list of models in this file. The API sends them,
 * all of them, in presentation order (`available_attribution_models`). A copy
 * here used to drift from the closed set the mart emits, and a model the mart
 * does not emit renders an EMPTY report rather than an error (plan
 * 2026-08-28-survey-blended-attribution-model, Besluit 6).
 */
export type AttributionModelOption =
  components["schemas"]["AttributionModelOptionDTO"];

/**
 * The account's dashboard colours (plan 2026-10-16-account-dashboard-
 * appearance). A preset key, or `custom` with its own page and block colour.
 * The presets' colours come from the API (`dashboard_surface_presets`); this
 * file keeps no copy of them.
 */
export type DashboardSurfacePresetKey = "classic" | "clean" | "soft" | "tinted" | "custom";

/** One preset as the API lists it, in picker order. */
export type DashboardSurfacePreset = {
  key: string;
  label: string;
  page_bg: string;
  block_bg: string;
};

/** The effective colours: always complete, lowercase `#rrggbb`. */
export type DashboardSurface = {
  preset: string;
  page_bg: string;
  block_bg: string;
};

export type PresetInfo = {
  key: ChartColorPreset;
  name: string;
  colors: string[];
};

export type PlatformPreferences = {
  chart_color_preset: ChartColorPreset;
  chart_custom_colors: string[] | null;
  chart_colors: string[];
  default_chart_type: ChartTypePreference;
  date_format_preset: DateFormatPreset;
  date_order_preset: DateOrderPreset;
  updated_at: string | null;
};

export type AccountPreferences = {
  chart_color_preset: ChartColorPreset | null;
  chart_custom_colors: string[] | null;
  default_chart_type: ChartTypePreference | null;
  date_format_preset: DateFormatPreset | null;
  date_order_preset: DateOrderPreset | null;
  has_overrides: boolean;
  /** Standing business context for the automated analyses. */
  analysis_instructions: string | null;
  /** The account's explicit choice, or null when it never made one. */
  default_attribution_model: string | null;
  /**
   * The model the numbers are actually built on — the choice above, or the
   * platform default when it is null. Read this when displaying which model a
   * report used; never re-implement the fallback in the UI, or the label and
   * the numbers can disagree.
   */
  effective_attribution_model: string;
  /**
   * Every model, in presentation order. An unavailable one stays in the list
   * (the picker shows it disabled, with its reason), so the customer knows it
   * exists. Labels come from here too; never from a copy in the frontend.
   *
   * Optional on purpose, although the current backend always sends it: the
   * deploy does not order frontend and backend (in deploy-staging and
   * deploy-prod the frontend job needs neither), so for a few minutes this
   * frontend can meet a backend that predates the field. Every reader has to
   * decide what to show then; see `modelOptionsOf` in AccountPreferences.
   */
  available_attribution_models?: AttributionModelOption[];
  /** The account's tile frame for every dashboard (plan 2026-10-16-tile-
   *  frame-style-settings); null = Verity's default. Optional: an older
   *  backend does not send them. */
  tile_title_rule?: boolean | null;
  tile_corners?: "rounded" | "square" | null;
  tile_outline?: boolean | null;
  tile_subtitle_width?: "full" | "narrow" | null;
  /** The account's own dashboard text styles per role (plan 2026-10-17-
   *  dashboard-text-style-complete). Optional: an older backend does not
   *  send them. */
  text_styles?: TextStylesDTO;
  /** The account's own dashboard colours; null = Verity's default. The two
   *  colours are set only with `custom`. Optional: an older backend does not
   *  send them. */
  dashboard_surface_preset?: DashboardSurfacePresetKey | null;
  dashboard_page_bg?: string | null;
  dashboard_block_bg?: string | null;
  updated_at: string | null;
};

export type EffectivePreferences = {
  chart_color_preset: ChartColorPreset;
  chart_colors: string[];
  default_chart_type: ChartTypePreference;
  date_format_preset: DateFormatPreset;
  date_order_preset: DateOrderPreset;
  source: "platform" | "account";
  /** The account level over Verity's default, per field with its source. */
  tile_frame?: { title_rule: boolean; corners: "rounded" | "square"; outline: boolean };
  tile_frame_source?: Record<"title_rule" | "corners" | "outline", "account" | "platform">;
  /** The account's dashboard colours over Verity's default, where they come
   *  from, and the presets the settings page offers. Optional: an older
   *  backend does not send them. */
  dashboard_surface?: DashboardSurface;
  dashboard_surface_source?: "account" | "platform";
  dashboard_surface_presets?: DashboardSurfacePreset[];
  /** The 8 colours an account-level `palette_N` text colour means: the
   *  account palette cycled, what a dashboard inherits as its theme. */
  dashboard_theme_colors?: string[];
  /** The account's text styles per role and attribute over Verity's
   *  default, and where each comes from. */
  text_styles?: TextStylesDTO;
  text_styles_source?: TextStylesSourceDTO;
};

export type UpdatePlatformPreferencesRequest = {
  chart_color_preset?: ChartColorPreset;
  chart_custom_colors?: string[];
  default_chart_type?: ChartTypePreference;
  date_format_preset?: DateFormatPreset;
  date_order_preset?: DateOrderPreset;
};

export type UpdateAccountPreferencesRequest = {
  chart_color_preset?: ChartColorPreset;
  chart_custom_colors?: string[];
  default_chart_type?: ChartTypePreference;
  date_format_preset?: DateFormatPreset;
  date_order_preset?: DateOrderPreset;
  use_platform_defaults?: boolean;
  /** The tile frame. PRESENCE decides: omit to leave it, null for Verity's
   *  default. Its own tier: `use_platform_defaults` leaves it alone. */
  tile_title_rule?: boolean | null;
  tile_corners?: "rounded" | "square" | null;
  tile_outline?: boolean | null;
  tile_subtitle_width?: "full" | "narrow" | null;
  /** The dashboard text styles per role. PRESENCE of an attribute
   *  decides, as for the tile frame; null = Verity's default. */
  text_styles?: TextStylesWrite["text_styles"];
  /** The dashboard colours. PRESENCE decides, as for the tile frame: omit to
   *  leave them, `dashboard_surface_preset: null` goes back to Verity's
   *  default (clears all three). A preset other than `custom` clears the two
   *  colours server-side; `custom` needs both. A colour too dark to read the
   *  dashboard's text on is a 422 whose `detail` is a sentence for the
   *  customer. `use_platform_defaults` leaves them alone. */
  dashboard_surface_preset?: DashboardSurfacePresetKey | null;
  dashboard_page_bg?: string | null;
  dashboard_block_bg?: string | null;
  /**
   * Standing business context for the automated analyses (max 4000 chars).
   * Omit to leave unchanged; send an empty string to clear it. It is not a
   * platform-default override, so `use_platform_defaults` leaves it intact.
   */
  analysis_instructions?: string;
  /**
   * Attribution model for revenue attributed to a marketing channel. Omit to
   * leave unchanged; send an empty string to fall back to the platform
   * default. Like `analysis_instructions`, it is not a platform-default
   * override, so `use_platform_defaults` leaves it intact. A model this
   * account cannot choose (`available: false`) is a 422 whose message is a
   * sentence for the customer.
   */
  default_attribution_model?: string;
};

/** Storage cap on `analysis_instructions`, mirroring the backend column. */
export const ANALYSIS_INSTRUCTIONS_MAX_CHARS = 4000;

// ============================================================================
// Reading profiles (per report audience)
// ============================================================================

/**
 * Reading profiles are the thin layer ABOVE the analyst's watch profiles
 * (plan 2026-09-09-reports-intelligence, Besluit 3). The watch profiles decide
 * what is measured and when something is a finding; a reading profile decides
 * how much of that a report shows a given READER.
 *
 * Generated from the backend schemas, so the closed sets (`min_tier`,
 * `depth`) and the 0..3 cap on `max_deep_dives` stay in step with the
 * validator that enforces them — a value this file invented on its own would
 * fail at the PUT, not at the keyboard.
 */
export type ReadingProfileBody = components["schemas"]["ReadingProfileBody"];
export type ReadingProfileEntry = components["schemas"]["ReadingProfileEntry"];
export type ReadingProfilesResponse =
  components["schemas"]["ReadingProfilesResponse"];

/**
 * The account's override map: audience key -> a COMPLETE profile.
 *
 * `NonNullable` because the generated `profiles` field is optional (the
 * backend defaults it to `{}`), while a caller here always sends one — even
 * if empty, which is the "reset every audience" request.
 */
export type ReadingProfileMap = NonNullable<
  components["schemas"]["ReadingProfilesUpdate"]["profiles"]
>;

/** `min_tier` and `depth`, narrowed from the generated body. */
export type ReadingTier = ReadingProfileBody["min_tier"];
export type ReadingDepth = ReadingProfileBody["depth"];

// ============================================================================
// Public API Functions (all authenticated users)
// ============================================================================

/**
 * List all available color presets.
 */
export async function listColorPresets(): Promise<PresetInfo[]> {
  return apiData<PresetInfo[]>("/admin/preferences/presets");
}

/**
 * Get effective preferences for the current account.
 * Returns merged preferences with account overrides applied on top of platform defaults.
 */
export async function getEffectivePreferences(): Promise<EffectivePreferences> {
  return apiData<EffectivePreferences>("/admin/preferences/effective");
}

// ============================================================================
// Platform Admin API Functions (require superadmin)
// ============================================================================

/**
 * Get platform-wide default preferences.
 */
export async function getPlatformPreferences(): Promise<PlatformPreferences> {
  return apiData<PlatformPreferences>("/admin/preferences/platform");
}

/**
 * Update platform-wide default preferences.
 */
export async function updatePlatformPreferences(
  request: UpdatePlatformPreferencesRequest
): Promise<PlatformPreferences> {
  return apiJson<PlatformPreferences>("/admin/preferences/platform", {
    method: "PUT",
    body: JSON.stringify(request),
  });
}

// ============================================================================
// Account Admin API Functions
// ============================================================================

/**
 * Get account-specific preference overrides.
 */
export async function getAccountPreferences(): Promise<AccountPreferences> {
  return apiData<AccountPreferences>("/admin/preferences/account");
}

/**
 * Update account-specific preference overrides.
 */
export async function updateAccountPreferences(
  request: UpdateAccountPreferencesRequest
): Promise<AccountPreferences> {
  return apiJson<AccountPreferences>("/admin/preferences/account", {
    method: "PUT",
    body: JSON.stringify(request),
  });
}

/**
 * Clear all account preference overrides (revert to platform defaults).
 */
export async function clearAccountPreferences(): Promise<void> {
  await apiJson("/admin/preferences/account", {
    method: "DELETE",
  });
}

// ============================================================================
// Reading Profile API Functions (account admin)
// ============================================================================

/**
 * The effective reading profile per audience, with `source` saying whether it
 * comes from the platform default or this account's override.
 *
 * Account admin only — the backend answers 403 to everyone else, and the
 * caller gets an `ApiError` carrying that status.
 */
export async function getReadingProfiles(): Promise<ReadingProfilesResponse> {
  return apiJson<ReadingProfilesResponse>("/admin/preferences/reading-profiles");
}

/**
 * Replace the account's override map WHOLE.
 *
 * An audience absent from `profiles` reverts to the platform default, and
 * `{}` resets every audience — so send every audience that must stay
 * customised, not only the one that just changed.
 */
export async function putReadingProfiles(
  profiles: ReadingProfileMap
): Promise<ReadingProfilesResponse> {
  return apiJson<ReadingProfilesResponse>("/admin/preferences/reading-profiles", {
    method: "PUT",
    body: JSON.stringify({ profiles }),
  });
}
